import {
  createInstance,
  logOnlyEventDispatcher,
  OptimizelyProvider
} from '@optimizely/react-sdk'
import { UserProperty } from 'utils/tracking/amplitude/amplitude.interface'
import { setUserProperties } from 'utils/tracking/amplitude'
import { observer } from 'mobx-react'
import { PropsWithChildren, useState } from 'react'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute, trackTeal } from 'utils/tracking'
import optimizelyErrorHandler from 'utils/tracking/optimizely/errorHandler'
import { trackWithSegment } from 'utils/url'
import { getUserExperimentsAndVariations } from 'utils/tracking/optimizely/isInAnyExperiment'
import isNotAffiliate from 'utils/formDependencies/conditions/isNotAffiliate'

// Relies on continous internet connection in contrast to the datafile approach
export const optimizelyClient = createInstance({
  sdkKey: process.env.REACT_APP_OPTIMIZELY_SDK_KEY,
  errorHandler: optimizelyErrorHandler,
  eventDispatcher: logOnlyEventDispatcher
})

export enum Experiments {
  TEST = 'test_catapult_flag_experiment',
  CAT_1043_REMOVE_LOADING_PAGE = 'cat-1043__remove_loading_page',
  CAT_1040_REPLACE_IBAN_PAGE_HEADLINE_TEST = 'test_catapult_iban_headline_replace_flag_experiment'
}

const CustomOptimizelyProvider = observer(
  ({ children }: PropsWithChildren<unknown>) => {
    const { rootStore } = useRootStore()
    const [trackInitialization, setTrackInitialization] = useState(true)

    if (
      !trackWithSegment() ||
      !isNotAffiliate(
        rootStore.traversal.data,
        rootStore.navigation.tenantContext
      )
    ) {
      return <>{children}</>
    }

    const userId = getCookie(COOKIE_KEY_FCID) || ''

    optimizelyClient.setUser({
      id: userId,
      attributes: { ...getOptimizelyTrackingAttribute(rootStore) }
    })

    optimizelyClient.onReady().then(() => {
      const { runningVariations, runningExperiments, segmentTrackingObject } =
        getUserExperimentsAndVariations(
          optimizelyClient,
          Object.values(Experiments),
          userId,
          rootStore
        )

      /* there is no way to find experiment and variation from opitmizelyEndUserId 
    for a new experiment we need to add a new event here each time if there are running experiments */

      setUserProperties({
        [UserProperty.OptimizelyEndUserId]: userId,
        [UserProperty.OptimizelyVariation]: runningVariations,
        [UserProperty.OptimizelyUserAttributes]: {
          ...getOptimizelyTrackingAttribute(rootStore)
        },
        [UserProperty.OptimizelyExperimentName]: runningExperiments
      })

      // This Proviver component is called twice when initializing Optimzely. We only want to send the event `optimizely_initialized` the first time

      const {
        navigation: { currentPageInSession }
      } = rootStore

      if (trackInitialization && currentPageInSession) {
        const initPathName = `/antrag/${currentPageInSession}`

        trackTeal({
          step: initPathName,
          transaction: {
            event: 'optimizely_initialized',
            label: initPathName,
            category: 'optimizely',
            optimizely: {
              ...segmentTrackingObject
            }
          }
        })
        setTrackInitialization(false)
      }
    })

    return (
      <OptimizelyProvider optimizely={optimizelyClient}>
        {children}
      </OptimizelyProvider>
    )
  }
)

export default CustomOptimizelyProvider
